<template>
	<teleport to="body">
		<div ref="wrapRef" :class="['text-input-wrap--fix', additionalClass]">
			<SongSettings v-if="showSongSettings" @change="handleSongSettingsChange" />
			<div
				:class="[
					'text-input-wrap',
					{ 'text-input-disabled': disabled, 'text-input-error': isError || !!attachError || !!formatError }
				]"
			>
				<div v-if="currentFile.size" class="text-input-attached">
					<attached-file
						:name="currentFile.name"
						:mimeType="currentFile.type"
						:size="currentFile.size"
						:preview-url="previewUrl"
						@remove-file="removeFile"
						:showRemoveFile="true"
					/>
					<p v-if="formatError" class="text-input-format-error">{{ formatError }}</p>
				</div>
				<textarea
					type="text-area"
					:class="['text-input']"
					:placeholder="placeholderText"
					@keydown.enter="enterKeydownHandler"
					@keyup.enter="sendMessage"
					@click="textAreaClickHandler"
					v-model="textMessage"
					name="message"
					:disabled="disabled"
				/>
				<div class="text-input-controll">
					<div class="text-input-controll-left">
						<reasoning-mode-toggle v-if="showReasoningToggle" @toggleReasoningMode="toggleReasoningMode" />
						<button-secret @button-secret-handler="buttonSecretHandler" v-if="showSecretChatButton" />
						<span :class="['text-input-counter', { 'disabled-counter': disabled }]"
							><span :class="['text-input-counter--current', { 'text-input-counter-error': isError }]">{{
								textMessageLength
							}}</span
							>/{{ MaxLength }}</span
						>
						<span v-if="attachError" class="text-input-attach-error">{{ attachError }}</span>
					</div>
					<div class="text-input-btn-group">
						<button-attached
							v-if="fileAvailable"
							ref="button-attached-child"
							@attach-error="onAttachError"
							@format-error="onFormatError"
						/>
						<button-send
							:can-send="canSend"
							:text-entered="textEntered"
							:sending-message="sendingMessage"
							@send-message="sendMessage"
							@get-text="getTextFromAudio"
						/>
					</div>
				</div>
			</div>
			<usage-bar v-if="showUsageBar" />
		</div>
	</teleport>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, useTemplateRef } from 'vue'
import { onBeforeRouteLeave, useRoute } from 'vue-router'
import { useCommonStore } from '@/stores/common'
import { useThreadStore } from '@/stores/thread'
import { useComposerStore } from '@/stores/composer'
import { useAuthRedirect } from '@/composables/useAuthRedirect'
import ReasoningModeToggle from '@/components/ReasoningModeToggle.vue'
import ButtonSecret from '@/components/ui/ButtonSecret.vue'
import ButtonSend from '@/components/ui/ButtonSend.vue'
import ButtonAttached from '@/components/ui/ButtonAttached.vue'
import AttachedFile from '@/components/AttachedFile.vue'
import AppConfig from '@/config'
import SongSettings, { type SongSettingsValue } from '@/components/ui/SongSettings.vue'
import UsageBar from '@/components/ui/UsageBar.vue'
import { useUsageStore } from '@/stores/usage'
import { brandName } from '@/config/tenant'

const route = useRoute()

defineOptions({
	inheritAttrs: false
})

const props = defineProps({
	placeholderText: {
		type: String,
		default: () => `Спросите ${brandName()}`
	},
	additionalClass: { type: String, default: '' },
	disabled: { type: Boolean, default: false }
})
const emit = defineEmits<{
	sendMessage: [text: string]
	sendSongSettings: [value: SongSettingsValue]
	buttonSecretHandler: []
}>()

const commonStore = useCommonStore()
const threadStore = useThreadStore()
const composerStore = useComposerStore()
const usageStore = useUsageStore()

const { ensureAuthenticated } = useAuthRedirect()

const textMessage = defineModel<string>({ default: '' })
const loading = computed(() => commonStore.loading)
const textMessageLength = computed(() => textMessage.value.length)
// Пустым считается и ввод из одних пробелов/переносов: отправлять такое сообщение нельзя.
const trimmedMessage = computed(() => textMessage.value.trim())
const isError = computed(() => textMessageLength.value > MaxLength.value)
const isHome = computed(() => route.name === 'Home')
const canSend = computed(
	() =>
		trimmedMessage.value.length > 0 &&
		!isError.value &&
		!loading.value &&
		!props.disabled &&
		!formatError.value &&
		// Гейт первого сообщения в треде редактирования изображения (нужна картинка) — только внутри треда.
		// Тип топика живёт в сторе до следующего clearProccess(), поэтому на главной он ещё «прошлый»:
		// без проверки маршрута заход в топик без отправки блокировал отправку с главной.
		!(!isHome.value && threadStore.imageEditMode && !threadStore.thread.messages.length && !currentFile.value.document)
)
const textEntered = computed(() => trimmedMessage.value.length > 0)
const sendingMessage = computed(() => commonStore.sendingMessage)
const fileAvailable = computed(() => threadStore.fileAvailable && !isHome.value)
const currentFile = computed(() => composerStore.currentFile)
const previewUrl = computed(() => currentFile.value.previewUrl ?? '')
const attachError = ref('')
const formatError = ref('')

function onAttachError(message: string) {
	attachError.value = message
}

function onFormatError(message: string) {
	formatError.value = message
}
const reasoningMode = computed(() => AppConfig.reasoningMode)
const secretModeAccess = computed(() => AppConfig.secretModeAccess)
const secretMode = computed(() => threadStore.secretMode)
const currentMessage = computed(() => composerStore.currentMessage)
// Тред считается загруженным, когда id из урла совпал с id в сторе. Признак синхронный и
// доступен с первого кадра: на /thread/:id до ответа getThread тип топика ещё неизвестен,
// и без этой проверки кнопка успевала мигнуть до того, как режим треда восстановится.
const threadLoaded = computed(() => {
	const routeId = route.params?.id
	if (!routeId) return true // /thread без id — новый чат, топика нет
	return Number(threadStore.thread?.id) === Number(routeId)
})
// Мышление доступно только в свободном чате: на главной и в треде без топика.
// Завязка на currentTopicType, а не на topicName: имя темы — отображаемая строка, она не
// персистится и зависит от загрузки /topics, поэтому после F5 бывала пустой в тематическом треде.
const showReasoningToggle = computed(
	() => reasoningMode.value && (route.name === 'Home' || (threadLoaded.value && threadStore.currentTopicType === null))
)
const showSecretChatButton = computed(() => secretModeAccess.value && (route.name === 'Home' || secretMode.value))
const isSongTopic = computed(() => threadStore.thread.topic?.type === 'song')
const songSettingsSent = ref(false)
const showSongSettings = computed(
	() => isSongTopic.value && route.name !== 'Home' && !props.disabled && !songSettingsSent.value
)
// Строка остатков — только потребительская редакция во внутреннем режиме авторизации.
// items === null (авторизованный без серверных лимитов) → бар не показываем, чтобы не мигал пустым.
const showUsageBar = computed(() => AppConfig.internalB2C && !!usageStore.items)
const MaxLength = computed(() => {
	const isMusicTopic = threadStore.thread.topic?.type === 'music'
	const isSongTopicLocal = threadStore.thread.topic?.type === 'song'
	const isNotHomePage = route.name !== 'Home'
	if ((isMusicTopic || isSongTopicLocal) && isNotHomePage) {
		return 500
	}
	return AppConfig.maxMessageLength
})
const buttonAttachedChild = useTemplateRef('button-attached-child')
const wrapRef = useTemplateRef<HTMLElement>('wrapRef')

// Инпут фиксирован (position: fixed) и телепортирован в body, поэтому не занимает места в
// потоке. Единственное, что резервирует место под него, — padding-bottom у .container. Его
// высота переменная (чип файла, настройки песни, ошибки, перенос текста, MTS-отступы), поэтому
// измеряем реальную высоту и прокидываем в CSS-переменную, которую использует .container.
let resizeObserver: ResizeObserver | null = null

function updateInputHeightVar(height: number) {
	document.documentElement.style.setProperty('--input-fixed-height', `${Math.ceil(height)}px`)
}

function textAreaClickHandler() {
	ensureAuthenticated()
}

// Enter в пустом (или пробельном) поле не должен вставлять перенос строки — отправлять нечего.
// Сама отправка по-прежнему на keyup, где решает canSend.
function enterKeydownHandler(event: KeyboardEvent) {
	if (!trimmedMessage.value) event.preventDefault()
}

async function sendMessage() {
	// Остаток заранее не проверяем: решение за сервером. Исчерпанный лимит приходит как
	// 403 GUEST_LIMIT_EXCEEDED / 402 и разбирается в handleLimitExceeded (services/limitExceeded.ts) —
	// гостю модалка входа, авторизованному шторка Pro. Так фронт не расходится с бэком в подсчёте.
	await ensureAuthenticated()
	if (canSend.value) {
		if (isSongTopic.value && songSettings.value) {
			emit('sendSongSettings', songSettings.value)
			songSettingsSent.value = true
		}
		emit('sendMessage', trimmedMessage.value)
		textMessage.value = ''
	}
}

async function toggleReasoningMode() {
	await ensureAuthenticated()
	composerStore.toggleReasoningMode()
}

async function buttonSecretHandler() {
	await ensureAuthenticated()
	emit('buttonSecretHandler')
}

function getTextFromAudio(message: string) {
	textMessage.value = message
}

function removeFile() {
	composerStore.clearCurrentFile()
	attachError.value = ''
	formatError.value = ''
	callClearInputAttached()
}

function callClearInputAttached() {
	if (buttonAttachedChild.value) {
		buttonAttachedChild.value.clearInput()
	}
}

const songSettings = ref<SongSettingsValue | null>(null)

function handleSongSettingsChange(value: SongSettingsValue) {
	songSettings.value = value
}

onMounted(async () => {
	textMessage.value = currentMessage.value
	if (wrapRef.value) {
		updateInputHeightVar(wrapRef.value.getBoundingClientRect().height)
		resizeObserver = new ResizeObserver((entries) => {
			updateInputHeightVar(entries[0].target.getBoundingClientRect().height)
		})
		resizeObserver.observe(wrapRef.value)
	}
})

onUnmounted(() => {
	resizeObserver?.disconnect()
	resizeObserver = null
	document.documentElement.style.removeProperty('--input-fixed-height')
})

onBeforeRouteLeave((to) => {
	if (to.name === 'threadRooms') {
		composerStore.setCurrentMessage(textMessage.value)
	} else {
		composerStore.clearCurrentMessage()
	}
})

defineExpose({ removeFile })
</script>

<style lang="scss" scoped>
.text-input {
	height: 140px;
	height: 84px;
	font: var(--font-input);
	width: 100%;
	position: relative;
	color: var(--text-primary);
	transition: $transition-border;
	outline: 1px solid var(--background-primary);
	resize: none;
	border: none;
	&::placeholder {
		font: var(--font-input);
		opacity: 50%;
	}
	&--bigMargin {
		margin-bottom: 64px;
		background-color: var(--background-primary);
	}
	&-wrap--fix {
		position: fixed;
		bottom: 0;
		// Боковые отступы совпадают с $padding-container у .main-content — инпут стоит
		// по одной вертикали с контентом.
		padding: 12px 16px 16px;
		width: 100%;
		background: var(--background-primary);
		transition: $transition-opacity;
		z-index: 10;
		@if $is-mts {
			// На главной MTS панель красится тем же градиентом, что и фон .home::before
			// (fixed, top: 110px до низа вьюпорта): прижат к низу и той же высоты — швов нет,
			// а плитки при скролле уходят под панель, а не просвечивают сквозь неё.
			&.text-input--home {
				background: var(--gradient-home) bottom / 100% calc(100dvh - 110px) no-repeat;
			}
		}
	}
	&-error {
		outline: 1px solid var(--status-error);
	}
	&-attach-error {
		font: var(--font-caption);
		color: var(--status-error);
		flex: 1;
		min-width: 0;
		margin-left: 4px;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	&-attached {
		display: flex;
		align-items: center;
		gap: 12px;
	}
	&-format-error {
		font: var(--font-caption);
		color: var(--status-error);
	}
	&-wrap {
		position: relative;
		max-width: 468px;
		padding: 16px;
		padding-bottom: 40px;
		margin: 0 auto;
		background: var(--background-primary);
		border-radius: var(--radius-lg);
		box-shadow: var(--shadow-card);
		border: 1px solid var(--border-subtle);
		margin-top: 10px;
	}
	&-controll {
		width: calc(100% - 4px);
		position: absolute;
		margin: 0 auto;
		left: 0;
		right: 0;
		background-color: var(--background-primary);
		bottom: 6px;
		padding-left: 14px;
		padding-right: 14px;
		padding-bottom: 8px;
		border-bottom-left-radius: 14px;
		border-bottom-right-radius: 14px;
		@include flex-space-between;
		&-left {
			@include flex-start;
			align-items: center;
			gap: 8px;
			flex: 1;
			min-width: 0;
		}
	}
	&-counter {
		font: var(--font-caption);
		opacity: 50%;
		transition: $transition-color;
		&-error {
			color: var(--status-error);
		}
	}
	&-btn-group > *:first-child {
		margin-right: 8px;
	}

	&-disabled {
		pointer-events: none;
		opacity: 0.5;
	}
}

.disabled-counter {
	display: none;
}

textarea:disabled {
	background-color: transparent;
}
</style>
